import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { SessionStepProps } from '../SessionFlowBase';
import styled from 'styled-components';
import SessionLineupsMetadata from './SessionLineupsMetadata';
import { Switch, FormControlLabel } from '@material-ui/core';
import SessionLineupsTabs from './SessionLineupsTabs';
import { PeriodType, ClassType } from '../../../api';
import { Player } from '../../../services/references';
import { TSession } from '../Sessions';
import { TLineupEntry, toLineupPlayer } from './SessionLineupsPlayer';
import { Prompt } from 'react-router';
import { useSnackbar } from 'notistack';
import MatchInfoButton from '../MatchInfoButton';
import { isFullID } from '../SessionEvents';
import useSession, { refreshSession } from '../../Util/useSession';
import { LineupReason, saveLineup } from '../../../services/sessionsUpload';
import { updateSessionFields } from '../../../services/sessions';
import { LineupEntry, Reference } from '../../../services/sessionModels';

interface Props extends SessionStepProps {}

const validateTeam = (lineup: Array<TLineupEntry>, session: TSession, team: Reference | undefined | null) => {
  if (!team) {
    return 'No team info';
  }
  const shirtNumbers = lineup.map((l) => l.shirtNumber).sort((a, b) => a - b);
  let results = [];
  for (let i = 0; i < shirtNumbers.length - 1; i++) {
    if (shirtNumbers[i + 1] === shirtNumbers[i]) {
      results.push(shirtNumbers[i]);
    }
  }
  if (results.length > 0) {
    let s = new Set(results);
    let it = s.values();
    let doubles = Array.from(it);
    return 'Multiple players for ' + team.name + ' with the same shirtnumber (' + doubles.join(', ') + ')';
  }
  const noID = lineup.filter((l) => l.shirtNumber <= 0);
  if (noID.length > 0) {
    return 'Not all shirtnumbers have been entered';
  }
};

const validateLineup = (lineup: Array<TLineupEntry>, session: TSession) => {
  const homeTeam = lineup?.filter((l) => l.type === ClassType.HomePlayer || l.type === ClassType.HomeKeeper);
  const awayTeam = lineup?.filter((l) => l.type === ClassType.AwayPlayer || l.type === ClassType.AwayKeeper);
  let ret = validateTeam(homeTeam, session, session?.homeTeam);
  if (!ret) {
    ret = validateTeam(awayTeam, session, session?.awayTeam);
  }
  if (!ret) {
    const players = lineup.map((l) => l.player).sort((a, b) => a?.id.localeCompare(b?.id || '') || 0);
    let results = [];
    for (let i = 0; i < players.length - 1; i++) {
      if (players[i + 1]?.id === players[i]?.id && players[i]?.id) {
        results.push(players[i]);
      }
    }
    if (results.length > 0) {
      let s = new Set(results);
      let it = s.values();
      let doubles = Array.from(it);
      return 'Players can not play for both teams (' + doubles.map((d) => d?.name || 'Unknown').join(', ') + ')';
    }
  }
  return ret;
};

const toLineupEntry = (entry: LineupEntry): TLineupEntry => ({
  id: String(entry.id),
  type: entry.type,
  player: entry.player ? { id: entry.player.id, name: entry.player.name } : null,
  substituted: entry.substituted ? { id: entry.substituted.id, name: entry.substituted.name } : null,
  periodIn: entry.periodIn,
  periodOut: entry.periodOut ?? undefined,
  inSeconds: entry.inSeconds ?? 0,
  outSeconds: entry.outSeconds ?? undefined,
  shirtNumber: entry.shirtNumber,
  reason: entry.reason ?? undefined,
  inEventId: entry.inEventId,
  outEventId: entry.outEventId,
});

const checkUpdateLineup = (lineup: Array<TLineupEntry>, nr: number, type: ClassType, team: string) => {
  let nrFound = lineup.filter((l) => l.type === type)?.length || 0;
  while (nrFound < nr) {
    lineup.push({
      inSeconds: 0,
      type: type,
      periodIn: PeriodType.FirstHalf,
      id: '-' + type + '-' + nrFound + '-' + team,
      shirtNumber: nrFound + (type === ClassType.HomeKeeper || type === ClassType.AwayKeeper ? 1 : 2),
      player: { id: '' },
    });
    nrFound++;
  }
  return lineup;
};

export interface CompoundedComponent extends React.ForwardRefExoticComponent<Props & React.RefAttributes<any>> {
  isComplete: (session: TSession) => boolean;
}

const SessionLineups = forwardRef<any, Props>(({ setNextEnabled, sessionid }, ref) => {
  const { session } = useSession(sessionid);
  const [fullID, setFullID] = useState(false);
  const [lineup, setLineup] = useState<Array<TLineupEntry> | undefined>();
  const [dirty, setDirty] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!fullID) setFullID(isFullID(session));
    // eslint-disable-next-line
  }, [session]);

  useEffect(() => {
    setNextEnabled(true);
  }, [setNextEnabled]);

  useEffect(() => {
    let baseLineup = session?.lineup?.slice()?.sort((a, b) => a.shirtNumber - b.shirtNumber).map(toLineupEntry) || [];
    if (session) {
      baseLineup = checkUpdateLineup(baseLineup, session.keeperCount || 1, ClassType.HomeKeeper, 'home');
      baseLineup = checkUpdateLineup(baseLineup, session.keeperCount || 1, ClassType.AwayKeeper, 'away');
      baseLineup = checkUpdateLineup(baseLineup, session.playerCount || 10, ClassType.HomePlayer, 'home');
      baseLineup = checkUpdateLineup(baseLineup, session.playerCount || 10, ClassType.AwayPlayer, 'away');
    }
    if (baseLineup.length > 0 && !lineup) setLineup(baseLineup);
  }, [session, lineup]);

  useImperativeHandle(
    ref,
    () => ({
      async onSave() {
        let ret = true;
        if (!fullID) {
          return true;
        }
        if (lineup && session) {
          const error = validateLineup(lineup, session);
          if (error) {
            enqueueSnackbar('Invalid lineup: ' + error, { variant: 'error', autoHideDuration: 3000 });
            return false;
          }
        }  

        const persistedId = (id: string) => (/^\d+$/.test(id) ? parseInt(id) : null);
        const keptIds = new Set((lineup || []).map((l) => persistedId(l.id)).filter((id) => id !== null));
        const removedIds = (session?.lineup || []).map((l) => l.id).filter((id) => !keptIds.has(id));

        await saveLineup(
          sessionid,
          removedIds,
          (lineup || []).map((l) => ({
            id: persistedId(l.id),
            type: l.type,
            playerId: l.player?.id || null,
            substitutedId: l.substituted?.id || null,
            periodIn: l.periodIn,
            periodOut: l.periodOut,
            inSeconds: l.inSeconds,
            outSeconds: l.outSeconds,
            inEventId: l.inEventId,
            outEventId: l.outEventId,
            shirtNumber: l.shirtNumber,
            reason: l.reason,
          }))
        ).then(() => refreshSession(sessionid)).then(
          () => {
            setDirty(false);
            ret = true;
          },
          () => {
            enqueueSnackbar('Failed to save lineup', { variant: 'error', autoHideDuration: 3000 });
            ret = false;
          }
        );
        return ret;
      },
    }),
    [sessionid, session, enqueueSnackbar, lineup, fullID]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFullID(event.target.checked);
    if (session) {
      updateSessionFields(session, { isFullId: event.target.checked }).then(() => refreshSession(sessionid));
    }
  };

  const onChangeLineupEntry = useCallback(
    (newEntry: TLineupEntry, prevEntry: TLineupEntry) => {
      // Check if prev entry is assigned to a label task, if so don't allow the change.
      const isAssignedToTask: boolean = session?.labelTasks?.find((task) => {
        return task.lineupEntry && String(task.lineupEntry.id) === prevEntry.id;
      }) !== undefined;

      if(isAssignedToTask) {
        enqueueSnackbar('Identity cannot be removed from lineup when assigned to a label task', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      const idx = lineup?.findIndex((l) => l.id === newEntry.id);

      if (idx !== undefined && idx !== -1 && lineup) {
        const newLineup = [...lineup];

        const substitutedIdx = lineup?.findIndex((l) => l.substituted && prevEntry.player && l.substituted.id === prevEntry.player.id);
        if (substitutedIdx !== undefined && substitutedIdx !== -1) {
          const substitutedEntry = lineup[substitutedIdx];
          newLineup[substitutedIdx] = { 
            ...substitutedEntry, 
            substituted: newEntry.player,
            outSeconds: newEntry.inSeconds,
            periodOut: newEntry.periodIn,
          };
        } else {
          console.log("[Warning] Trying to update an unknown substituted player in onChangeLineupEntry.")
        }

        newLineup[idx] = newEntry;
        setLineup(newLineup);
        setDirty(true);
      } else {
        console.log('Could not find lineup entry');
      }
    },
    [session, enqueueSnackbar, lineup]
  );

  const onDeleteRedCard = useCallback(
    (entry: TLineupEntry) => {
      const idx = lineup?.findIndex((l) => l.id === entry.id);
      if (idx !== undefined && idx !== -1 && lineup) {
        const newLineup = [...lineup];
        newLineup[idx] = { 
          ...entry, 
          periodOut: undefined, 
          outSeconds: undefined, 
          outEventId: undefined,
        };
        setLineup(newLineup);
        setDirty(true);
      }
    },
    [lineup]
  );

  const onDeleteSubstitution = useCallback(
    (entry: TLineupEntry) => {
      // Check if entry is assigned to a label task, if so don't allow the deletion.
      const isAssignedToTask: boolean = session?.labelTasks?.find((task) => {
        return task.lineupEntry && String(task.lineupEntry.id) === entry.id;
      }) !== undefined;

      if(isAssignedToTask) {
        enqueueSnackbar('Identity cannot be removed from lineup when assigned to a label task', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      const idx = lineup?.findIndex((l) => l.id === entry.id);
      if (idx !== undefined && idx !== -1 && lineup) {
        const newLineup = [...lineup];
        const substitutedIdx = lineup?.findIndex((l) => l.substituted?.id === entry.player?.id);
        
        if (substitutedIdx !== undefined && substitutedIdx !== -1) {
          const substitutedRecord = lineup[substitutedIdx];
          newLineup[substitutedIdx] = {
            ...substitutedRecord,
            outSeconds: undefined,
            periodOut: undefined,
            substituted: undefined,
            outEventId: undefined,
            reason: undefined,
          };
        } else {
          console.log("[Warning] Trying to update an unknown substituted player in onDeleteSubstitution.")
        }
        newLineup.splice(idx, 1);
        setLineup(newLineup);
        setDirty(true);
      }
    },
    [session, enqueueSnackbar, lineup]
  );

  const onAddExit = useCallback(
    (out: TLineupEntry, reason: LineupReason, outSeconds: number, period: PeriodType) => {
      const idx = lineup?.findIndex((l) => l.id === out.id);
      if (idx !== undefined && idx !== -1 && lineup) {
        const newLineup = [...lineup];
        newLineup[idx] = { ...out, outSeconds: outSeconds, reason: reason, periodOut: period };
        setLineup(newLineup);
        setDirty(true);
      }
    },
    [lineup]
  );

  const onAddSubstitution = useCallback(
    (out: TLineupEntry, outSeconds: number, period: PeriodType, playerIn: Player) => {
      const idx = lineup?.findIndex((l) => l.id === out.id);
      if (idx !== undefined && idx !== -1 && lineup) {
        const newLineup = [...lineup];
        newLineup[idx] = {
          ...out,
          outSeconds: outSeconds,
          reason: LineupReason.Substitution,
          periodOut: period,
          substituted: toLineupPlayer(playerIn),
        };
        newLineup.push({
          player: toLineupPlayer(playerIn),
          inSeconds: outSeconds,
          periodIn: period,
          shirtNumber: playerIn.lastKnownNumber || 0,
          id: idx + '-sub',
          type: out.type,
        });
        setLineup(newLineup);
        setDirty(true);
      }
    },
    [lineup]
  );

  return (
    <Container>
      <Prompt when={dirty} message="You have unsaved changes to the lineup, are you sure?" />

      <MetadataContainer>
        <SessionLineupsMetadata session={session || undefined} />
      </MetadataContainer>
      <SwitchContainer>
        <FormControlLabel
          control={<Switch checked={fullID} onChange={handleChange} name="checkedB" color="primary" />}
          label="Full ID tracking"
        />
        {session?.matchInfoUrl && <MatchInfoButton matchInfoUrl={session.matchInfoUrl} />}
      </SwitchContainer>
      {fullID && (
        <FormContainer>
          <SessionLineupsTabs
            session={session || undefined}
            lineup={lineup}
            onChangeLineupEntry={onChangeLineupEntry}
            onDeleteRedCard={onDeleteRedCard}
            onDeleteSubstitution={onDeleteSubstitution}
            onAddExit={onAddExit}
            onAddSubstitution={onAddSubstitution}
          />
        </FormContainer>
      )}
    </Container>
  );
}) as CompoundedComponent;

const MetadataContainer = styled.div`
  margin-bottom: 32px;
  margin-left: 6px;
  margin-right: 6px;
`;

const SwitchContainer = styled.div`
  margin-left: 16px;
`;

const FormContainer = styled.div`
  margin: 16px;
`;

const Container = styled.div`
  width: 100%;
  margin: auto;
  margin-top: 64px;
`;

SessionLineups.isComplete = () => {
  return true;
};

export default SessionLineups;
